import { ErrorState, LoadingState } from '@playforge/ui';
import { Badge, BlockStack, Button, Card, InlineStack, Page, Text } from '@shopify/polaris';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api-client';
import { useApi } from '../lib/use-api';
import type { FeaturesView, MerchantProfile, SubscriptionPlanId } from '../lib/types';

const SUPPORT_EMAIL = 'business.project.data@gmail.com';

/** Plans that include live chat (free + starter are email-only). */
const CHAT_PLANS: SubscriptionPlanId[] = ['growth', 'professional', 'enterprise'];

function EmailSupportCard({ shop }: { shop: string | null }): JSX.Element {
  const subject = encodeURIComponent(`PlayForge support${shop ? ` — ${shop}` : ''}`);
  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack gap="200" blockAlign="center">
          <Text as="h2" variant="headingMd">
            📧 Email support
          </Text>
          <Badge tone="success">Included</Badge>
        </InlineStack>
        <Text as="p" tone="subdued">
          Email our team any time — we typically reply within 1 business day. Include your store URL
          and a description of the issue so we can help faster.
        </Text>
        <InlineStack>
          <Button variant="primary" url={`mailto:${SUPPORT_EMAIL}?subject=${subject}`} external>
            Email {SUPPORT_EMAIL}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

function LiveChatCard({ enabled, shop }: { enabled: boolean; shop: string | null }): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!enabled) {
    return (
      <Card>
        <BlockStack gap="300">
          <InlineStack gap="200" blockAlign="center">
            <Text as="h2" variant="headingMd">
              💬 Live chat
            </Text>
            <Badge>Growth and above</Badge>
          </InlineStack>
          <Text as="p" tone="subdued">
            Get real-time help from our team. Live chat is available on the Growth, Professional and
            Enterprise plans.
          </Text>
          <InlineStack>
            <Button variant="primary" onClick={() => navigate('/billing')}>
              Upgrade to unlock live chat
            </Button>
          </InlineStack>
        </BlockStack>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack gap="200" blockAlign="center">
          <Text as="h2" variant="headingMd">
            💬 Live chat
          </Text>
          <Badge tone="success">Included</Badge>
        </InlineStack>
        <Text as="p" tone="subdued">
          Chat with our support team in real time.
        </Text>
        {open ? (
          <iframe
            title="PlayForge live chat"
            src={`/support-chat?shop=${encodeURIComponent(shop ?? '')}`}
            style={{
              display: 'block',
              width: '100%',
              maxWidth: 380,
              height: 560,
              maxHeight: '70vh',
              border: '1px solid #e1e3e5',
              borderRadius: 14,
            }}
          />
        ) : null}
        <InlineStack>
          <Button {...(open ? {} : { variant: 'primary' as const })} onClick={() => setOpen((o) => !o)}>
            {open ? 'Close chat' : 'Start live chat'}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

export function SupportPage(): JSX.Element {
  const features = useApi(() => apiClient.get<FeaturesView>('/billing/features'), []);
  const profile = useApi(() => apiClient.get<MerchantProfile>('/merchant/profile'), []);

  const plan = features.data?.plan;
  const chatEnabled = plan ? CHAT_PLANS.includes(plan) : false;
  const shop = profile.data?.shopDomain ?? null;

  return (
    <Page title="Support" subtitle="Get help from the PlayForge team">
      {features.loading ? <LoadingState label="Loading support options" /> : null}
      {features.error ? <ErrorState message={features.error.message} onRetry={features.refetch} /> : null}

      {features.data ? (
        <BlockStack gap="400">
          <Card>
            <InlineStack align="space-between" blockAlign="center">
              <Text as="span" tone="subdued">
                Your plan
              </Text>
              <Badge tone="info">{(plan ?? 'free').toUpperCase()}</Badge>
            </InlineStack>
          </Card>
          <EmailSupportCard shop={shop} />
          <LiveChatCard enabled={chatEnabled} shop={shop} />
        </BlockStack>
      ) : null}
    </Page>
  );
}
